import React, { useState, useRef } from 'react';
import { User, Phone, Mail, FileText, Upload, ChevronDown, ChevronUp, X, Receipt, Eye, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SAT Regimen Fiscal catalog (common ones)
const REGIMEN_OPTIONS = [
  { value: '601', label: '601 - General de Ley PM' },
  { value: '603', label: '603 - Personas Morales Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios' },
  { value: '606', label: '606 - Arrendamiento' },
  { value: '608', label: '608 - Demás Ingresos' },
  { value: '610', label: '610 - Residentes en el Extranjero' },
  { value: '612', label: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: '614', label: '614 - Ingresos por Intereses' },
  { value: '616', label: '616 - Sin Obligaciones Fiscales' },
  { value: '620', label: '620 - Sociedades Cooperativas de Producción' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '622', label: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: '625', label: '625 - Régimen de las Actividades Empresariales con Ingresos a través de Plataformas Tecnológicas' },
  { value: '626', label: '626 - Régimen Simplificado de Confianza' },
];

const USO_CFDI_OPTIONS = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I02', label: 'I02 - Mobiliario y equipo de oficina' },
  { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
  { value: 'D01', label: 'D01 - Honorarios médicos' },
  { value: 'D02', label: 'D02 - Gastos médicos por incapacidad' },
  { value: 'D03', label: 'D03 - Gastos funerales' },
  { value: 'D04', label: 'D04 - Donativos' },
  { value: 'D10', label: 'D10 - Pagos por servicios educativos' },
  { value: 'P01', label: 'P01 - Por definir' },
  { value: 'S01', label: 'S01 - Sin efectos fiscales' },
  { value: 'CP01', label: 'CP01 - Pagos' },
];

const FORMA_PAGO_OPTIONS = [
  { value: '01', label: '01 - Efectivo' },
  { value: '02', label: '02 - Cheque nominativo' },
  { value: '03', label: '03 - Transferencia electrónica' },
  { value: '04', label: '04 - Tarjeta de crédito' },
  { value: '28', label: '28 - Tarjeta de débito' },
  { value: '99', label: '99 - Por definir' },
];

export function CustomerForm({ customerData, onChange, invoiceRequested, onInvoiceRequestedChange, invoiceFiscalData, onInvoiceFiscalDataChange }) {
  const [showFiscalInfo, setShowFiscalInfo] = useState(false);
  const [fiscalFile, setFiscalFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [fiscalExtracted, setFiscalExtracted] = useState(null);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    onChange({ ...customerData, [field]: value });
  };

  const uploadFileToServer = async (file, fileType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    
    const response = await axios.post(`${API}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data.url;
  };

  const analyzeFiscalDocument = async (fileUrl) => {
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file_url', fileUrl);
      const response = await axios.post(`${API}/analyze-fiscal`, formData);
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setFiscalExtracted(data);
        const found = [data.rfc, data.nombre, data.regimen, data.codigo_postal].filter(Boolean).length;
        if (found > 0) {
          toast.success(`Se extrajeron ${found} datos fiscales`, {
            description: 'Se copiarán al formulario de factura automáticamente'
          });
        } else {
          toast.info('No se pudieron extraer datos automáticamente', {
            description: 'Ingresa los datos de facturación manualmente'
          });
        }
      }
    } catch (err) {
      console.error('Error analyzing fiscal doc:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFiscalFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato no válido', {
        description: 'Solo se permiten archivos PDF, JPG o PNG'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Archivo muy grande', {
        description: 'El archivo no debe superar los 5MB'
      });
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload to server
      const fileUrl = await uploadFileToServer(file, 'fiscal');
      
      setFiscalFile({
        name: file.name,
        type: file.type,
        size: file.size,
        url: fileUrl
      });
      
      // Update customerData with fiscal file URL
      onChange({ 
        ...customerData, 
        fiscalUrl: fileUrl,
        fiscalFile: {
          name: file.name,
          type: file.type,
          size: file.size,
          url: fileUrl
        }
      });
      
      toast.success('Archivo cargado', {
        description: file.name
      });

      // Analyze fiscal document to extract data
      analyzeFiscalDocument(fileUrl);
    } catch (error) {
      console.error('Error uploading fiscal file:', error);
      toast.error('Error al cargar el archivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFiscalFile = () => {
    setFiscalFile(null);
    setFiscalExtracted(null);
    onChange({ ...customerData, fiscalFile: null, fiscalUrl: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreviewFiscal = () => {
    if (fiscalFile?.url) {
      window.open(`${BACKEND_URL}${fiscalFile.url}`, '_blank');
    }
  };

  const handleInvoiceToggle = (value) => {
    if (value && !fiscalFile) {
      toast.error('Constancia fiscal requerida', {
        description: 'Primero debes cargar tu constancia de situación fiscal antes de solicitar factura.'
      });
      return;
    }
    // Auto-fill invoice fields from extracted fiscal data
    if (value && fiscalExtracted && onInvoiceFiscalDataChange) {
      const autoFilled = { ...(invoiceFiscalData || {}) };
      if (fiscalExtracted.rfc && !autoFilled.receptor_rfc) autoFilled.receptor_rfc = fiscalExtracted.rfc;
      if (fiscalExtracted.nombre && !autoFilled.receptor_nombre) autoFilled.receptor_nombre = fiscalExtracted.nombre;
      if (fiscalExtracted.regimen && !autoFilled.receptor_regimen) autoFilled.receptor_regimen = fiscalExtracted.regimen;
      if (fiscalExtracted.codigo_postal && !autoFilled.receptor_cp) autoFilled.receptor_cp = fiscalExtracted.codigo_postal;
      onInvoiceFiscalDataChange(autoFilled);
    }
    onInvoiceRequestedChange && onInvoiceRequestedChange(value);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-gradient-to-r from-[#0f133a]/80 to-[#1a1f50]/80 border border-white/10 rounded-xl p-5 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
          <User className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Datos del Cliente
        </h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-white text-sm flex items-center gap-2">
            <User className="w-3 h-3 text-[#aa2aff]" />
            Nombre
          </Label>
          <Input
            type="text"
            value={customerData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Nombre completo"
            className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
            data-testid="input-customer-name"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-white text-sm flex items-center gap-2">
            <Phone className="w-3 h-3 text-[#aa2aff]" />
            Teléfono
          </Label>
          <Input
            type="tel"
            value={customerData.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="+52 555 123 4567"
            className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
            data-testid="input-customer-phone"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-white text-sm flex items-center gap-2">
            <Mail className="w-3 h-3 text-[#aa2aff]" />
            Email
          </Label>
          <Input
            type="email"
            value={customerData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="correo@ejemplo.com"
            className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
            data-testid="input-customer-email"
          />
        </div>
      </div>

      {/* Fiscal Information Toggle */}
      <div className="mt-5 pt-4 border-t border-white/10">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setShowFiscalInfo(!showFiscalInfo)}
        >
          <div className="flex items-center gap-3">
            <FileText className="w-4 h-4 text-[#aa2aff]" />
            <span className="text-white text-sm font-medium">Agregar información Fiscal</span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={showFiscalInfo}
              onCheckedChange={setShowFiscalInfo}
              className="data-[state=checked]:bg-[#aa2aff]"
              data-testid="toggle-fiscal-info"
            />
            {showFiscalInfo ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>

        {/* Fiscal Information Section */}
        {showFiscalInfo && (
          <div className="mt-4 p-4 bg-[#080c2d]/50 rounded-lg border border-[#272b96]/50 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-3">
              <Label className="text-white text-sm flex items-center gap-2">
                <Upload className="w-3 h-3 text-[#aa2aff]" />
                Subir Constancia de Situación Fiscal
              </Label>
              
              {!fiscalFile ? (
                <div 
                  className="border-2 border-dashed border-[#272b96] rounded-lg p-6 text-center hover:border-[#aa2aff] transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFiscalFileChange}
                    className="hidden"
                    data-testid="input-fiscal-file"
                  />
                  <Upload className="w-8 h-8 text-[#aa2aff] mx-auto mb-2" />
                  <p className="text-slate-300 text-sm">
                    {isUploading ? 'Cargando...' : 'Haz clic o arrastra tu archivo aquí'}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    PDF, JPG o PNG (máx. 5MB)
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-[#0f133a] rounded-lg border border-[#272b96]">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                      <FileText className="w-5 h-5 text-[#aa2aff]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium truncate max-w-[200px]">
                        {fiscalFile.name}
                      </p>
                      <p className="text-slate-500 text-xs">
                        {formatFileSize(fiscalFile.size)}
                        {isAnalyzing && <span className="ml-2 text-[#aa2aff]">Analizando...</span>}
                        {fiscalExtracted && !isAnalyzing && (
                          <span className="ml-2 text-green-400">
                            {[fiscalExtracted.rfc, fiscalExtracted.nombre, fiscalExtracted.regimen, fiscalExtracted.codigo_postal].filter(Boolean).length} datos extraídos
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePreviewFiscal}
                      className="text-[#aa2aff] hover:text-[#aa2aff]/80 hover:bg-[#aa2aff]/10"
                      data-testid="btn-preview-fiscal"
                      title="Vista previa"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFiscalFile}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                      data-testid="btn-remove-fiscal-file"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Invoice Request Section */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => handleInvoiceToggle(!invoiceRequested)}
        >
          <div className="flex items-center gap-3">
            <Receipt className="w-4 h-4 text-green-400" />
            <span className="text-white text-sm font-medium">Solicitar Factura (CFDI 4.0)</span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={invoiceRequested || false}
              onCheckedChange={(v) => handleInvoiceToggle(v)}
              className="data-[state=checked]:bg-green-500"
              data-testid="toggle-invoice-request"
            />
            {invoiceRequested ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>

        {invoiceRequested && (
          <div className="mt-4 p-4 bg-[#080c2d]/50 rounded-lg border border-green-500/20 animate-in slide-in-from-top-2 duration-200">
            <p className="text-slate-400 text-xs mb-3">Datos fiscales del receptor para la factura</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-white text-xs">RFC *</Label>
                <Input
                  type="text"
                  value={invoiceFiscalData?.receptor_rfc || ''}
                  onChange={(e) => onInvoiceFiscalDataChange({ ...invoiceFiscalData, receptor_rfc: e.target.value.toUpperCase() })}
                  placeholder="XAXX010101000"
                  maxLength={13}
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 text-sm"
                  data-testid="input-invoice-rfc"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-white text-xs">Nombre o Razón Social *</Label>
                <Input
                  type="text"
                  value={invoiceFiscalData?.receptor_nombre || ''}
                  onChange={(e) => onInvoiceFiscalDataChange({ ...invoiceFiscalData, receptor_nombre: e.target.value })}
                  placeholder="Nombre fiscal"
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 text-sm"
                  data-testid="input-invoice-nombre"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-white text-xs">Régimen Fiscal *</Label>
                <select
                  value={invoiceFiscalData?.receptor_regimen || '612'}
                  onChange={(e) => onInvoiceFiscalDataChange({ ...invoiceFiscalData, receptor_regimen: e.target.value })}
                  className="w-full h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-xs"
                  data-testid="select-invoice-regimen"
                >
                  {REGIMEN_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#0f133a] text-white">{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-white text-xs">Código Postal Fiscal *</Label>
                <Input
                  type="text"
                  value={invoiceFiscalData?.receptor_cp || ''}
                  onChange={(e) => onInvoiceFiscalDataChange({ ...invoiceFiscalData, receptor_cp: e.target.value })}
                  placeholder="01000"
                  maxLength={5}
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 text-sm"
                  data-testid="input-invoice-cp"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-white text-xs">Uso CFDI</Label>
                <select
                  value={invoiceFiscalData?.receptor_uso_cfdi || 'G03'}
                  onChange={(e) => onInvoiceFiscalDataChange({ ...invoiceFiscalData, receptor_uso_cfdi: e.target.value })}
                  className="w-full h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-xs"
                  data-testid="select-invoice-uso-cfdi"
                >
                  {USO_CFDI_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#0f133a] text-white">{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-white text-xs">Forma de Pago</Label>
                <select
                  value={invoiceFiscalData?.forma_pago || '03'}
                  onChange={(e) => onInvoiceFiscalDataChange({ ...invoiceFiscalData, forma_pago: e.target.value })}
                  className="w-full h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-xs"
                  data-testid="select-invoice-forma-pago"
                >
                  {FORMA_PAGO_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#0f133a] text-white">{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <p className="text-amber-400/80 text-xs mt-3 flex items-center gap-1.5">
              <Receipt className="w-3 h-3" />
              Tu solicitud será revisada y aprobada por el administrador antes de emitir la factura.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerForm;
